import React, { useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useAbortableLoad, isCanceled } from '../hooks/useAbortableLoad';
import { useThemeColors, getPrimaryButtonStyles } from '../hooks/useThemeColors';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { 
  User, Mail, Phone, Lock, Camera, Trash2, 
  Loader2, Save, AlertCircle, Shield, Calendar, CheckCircle, Briefcase
} from 'lucide-react';
import PasswordStrengthIndicator from '../components/PasswordStrengthIndicator';

const PerfilPage = () => {
  const { api, user: authUser } = useAuth();
  const themeColors = useThemeColors();
  const fileInputRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  
  const [perfil, setPerfil] = useState(null);
  const [error, setError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  
  const [formData, setFormData] = useState({
    nombre: '',
    correo: '',
    telefono: '',
    cargo: '',
  });
  
  const [passwordData, setPasswordData] = useState({
    password_actual: '',
    password_nueva: '',
    password_confirmar: '',
  });

  // Cargar perfil
  const fetchPerfil = async (signal) => {
    setLoading(true);
    try {
      const response = await api.get('/perfil', { signal });
      setPerfil(response.data);
      setFormData({
        nombre: response.data.nombre || '',
        correo: response.data.correo || '',
        telefono: response.data.telefono || '',
        cargo: response.data.cargo || '',
      });
    } catch (err) {
      if (isCanceled(err)) return;
      console.error('Error fetching perfil:', err);
      toast.error('Error al cargar el perfil');
    } finally {
      setLoading(false);
    }
  };

  useAbortableLoad((signal) => fetchPerfil(signal), []);

  // Guardar información personal
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      await api.put('/perfil', formData);
      toast.success('Perfil actualizado correctamente');
      fetchPerfil();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al actualizar el perfil');
    } finally {
      setSaving(false);
    }
  };

  // Cambiar contraseña
  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError('');

    if (passwordData.password_nueva !== passwordData.password_confirmar) {
      setPasswordError('Las contraseñas nuevas no coinciden');
      return;
    }

    if (passwordData.password_nueva.length < 8) {
      setPasswordError('La contraseña debe tener al menos 8 caracteres');
      return;
    }

    setChangingPassword(true);

    try {
      await api.put('/perfil/cambiar-password', passwordData);
      toast.success('Contraseña actualizada correctamente');
      setPasswordData({
        password_actual: '',
        password_nueva: '',
        password_confirmar: '',
      });
    } catch (err) {
      setPasswordError(err.response?.data?.detail || 'Error al cambiar la contraseña');
    } finally {
      setChangingPassword(false);
    }
  };

  // Subir foto
  const handlePhotoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validar tipo
    if (!file.type.startsWith('image/')) {
      toast.error('Solo se permiten imágenes');
      event.target.value = '';
      return;
    }

    // Validar tamaño (2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('La imagen no puede superar 2MB');
      event.target.value = '';
      return;
    }

    setUploadingPhoto(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/perfil/foto', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setPerfil(prev => ({ ...prev, foto_url: response.data.foto_url + '?t=' + Date.now() }));
      toast.success('Foto actualizada');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al subir la foto');
    } finally {
      setUploadingPhoto(false);
      event.target.value = '';
    }
  };

  // Eliminar foto
  const handleDeletePhoto = async () => {
    if (!window.confirm('¿Está seguro de eliminar su foto de perfil?')) return;

    try {
      await api.delete('/perfil/foto');
      setPerfil(prev => ({ ...prev, foto_url: null }));
      toast.success('Foto eliminada');
    } catch (err) {
      toast.error('Error al eliminar la foto');
    }
  };

  // Formatear fecha
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-PE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Obtener URL de la foto
  const getPhotoUrl = () => {
    if (!perfil?.foto_url) return null;
    const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
    if (perfil.foto_url.startsWith('/api')) {
      return baseUrl + perfil.foto_url;
    }
    return perfil.foto_url;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn max-w-4xl mx-auto" data-testid="perfil-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900">Mi Perfil</h1>
        <p className="text-slate-500 text-sm mt-1">
          Administra tu información personal y configuración de cuenta
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Columna izquierda - Foto y datos básicos */}
        <div className="lg:col-span-1 space-y-6">
          {/* Card de foto */}
          <Card className="border-slate-200">
            <CardContent className="p-6 text-center">
              <div className="relative inline-block">
                <div className="w-32 h-32 mx-auto rounded-full bg-slate-100 flex items-center justify-center overflow-hidden border-4 border-white shadow-lg">
                  {getPhotoUrl() ? (
                    <img 
                      src={getPhotoUrl()} 
                      alt="Avatar" 
                      className="w-full h-full object-cover"
                      data-testid="profile-photo"
                    />
                  ) : (
                    <User className="w-16 h-16 text-slate-400" />
                  )}
                </div>
                
                {/* Botón de cámara */}
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handlePhotoUpload}
                  accept="image/*"
                  className="hidden"
                  data-testid="photo-input"
                />
                <Button
                  size="icon"
                  className="absolute bottom-0 right-0 rounded-full shadow-md text-white"
                  style={{ backgroundColor: themeColors.primario }}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingPhoto}
                  data-testid="upload-photo-btn"
                >
                  {uploadingPhoto ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Camera className="w-4 h-4" />
                  )}
                </Button>
              </div>

              <h3 className="mt-4 font-semibold text-lg text-slate-900">{perfil?.nombre}</h3>
              <p className="text-sm text-slate-500">{perfil?.correo}</p>
              
              <Badge 
                className="mt-2"
                style={{ 
                  backgroundColor: `${themeColors.primario}20`,
                  color: themeColors.primario 
                }}
              >
                {perfil?.rol}
              </Badge>

              {getPhotoUrl() && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-4 text-red-500 hover:text-red-700"
                  onClick={handleDeletePhoto}
                  data-testid="delete-photo-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Eliminar foto
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Info adicional */}
          <Card className="border-slate-200">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center gap-3 text-sm">
                <Calendar className="w-4 h-4 text-slate-400" />
                <div>
                  <p className="text-slate-500">Miembro desde</p>
                  <p className="font-medium text-slate-900">{formatDate(perfil?.created_at)}</p>
                </div>
              </div>
              <Separator />
              <div className="flex items-center gap-3 text-sm">
                <Shield className="w-4 h-4 text-slate-400" />
                <div>
                  <p className="text-slate-500">2FA</p>
                  <p className="font-medium text-slate-900">
                    {perfil?.two_factor_enabled ? (
                      <span className="text-green-600 flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" /> Activado
                      </span>
                    ) : 'Desactivado'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Columna derecha - Formularios */}
        <div className="lg:col-span-2 space-y-6">
          {/* Información Personal */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <User className="w-5 h-5" />
                Información Personal
              </CardTitle>
              <CardDescription>
                Actualiza tu información de contacto
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <div className="space-y-2">
                  <Label htmlFor="nombre">Nombre completo</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="nombre"
                      data-testid="nombre-input"
                      className="pl-10"
                      value={formData.nombre}
                      onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="correo">Correo electrónico</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="correo"
                      type="email"
                      data-testid="correo-input"
                      className="pl-10"
                      value={formData.correo}
                      onChange={(e) => setFormData({ ...formData, correo: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="telefono">Teléfono</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        id="telefono"
                        data-testid="telefono-input"
                        className="pl-10"
                        value={formData.telefono}
                        onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                        placeholder="999 999 999"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="cargo">Cargo</Label>
                    <div className="relative">
                      <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        id="cargo"
                        data-testid="cargo-input"
                        className="pl-10"
                        value={formData.cargo}
                        onChange={(e) => setFormData({ ...formData, cargo: e.target.value })}
                        placeholder="Ej: Gerente de Ventas"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button
                    type="submit"
                    disabled={saving}
                    data-testid="guardar-perfil-btn"
                    className="text-white"
                    {...getPrimaryButtonStyles(themeColors)}
                  >
                    {saving ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    Guardar cambios
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Cambiar Contraseña */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Lock className="w-5 h-5" />
                Cambiar Contraseña
              </CardTitle>
              <CardDescription>
                Asegúrate de usar una contraseña segura de al menos 8 caracteres
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleChangePassword} className="space-y-4">
                {passwordError && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{passwordError}</AlertDescription>
                  </Alert>
                )}

                <div className="space-y-2">
                  <Label htmlFor="password_actual">Contraseña actual</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="password_actual"
                      type="password"
                      data-testid="password-actual-input"
                      className="pl-10"
                      placeholder="••••••••"
                      value={passwordData.password_actual}
                      onChange={(e) => setPasswordData({ ...passwordData, password_actual: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="password_nueva">Nueva contraseña</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        id="password_nueva"
                        type="password"
                        data-testid="password-nueva-input"
                        className="pl-10"
                        placeholder="••••••••"
                        value={passwordData.password_nueva}
                        onChange={(e) => setPasswordData({ ...passwordData, password_nueva: e.target.value })}
                        required
                        minLength={8}
                      />
                    </div>
                    <PasswordStrengthIndicator password={passwordData.password_nueva} />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password_confirmar">Confirmar contraseña</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        id="password_confirmar"
                        type="password"
                        data-testid="password-confirmar-input"
                        className="pl-10"
                        placeholder="••••••••"
                        value={passwordData.password_confirmar}
                        onChange={(e) => setPasswordData({ ...passwordData, password_confirmar: e.target.value })}
                        required
                        minLength={8}
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button
                    type="submit"
                    disabled={changingPassword}
                    data-testid="cambiar-password-btn"
                    className="text-white"
                    {...getPrimaryButtonStyles(themeColors)}
                  >
                    {changingPassword ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Lock className="w-4 h-4 mr-2" />
                    )}
                    Cambiar contraseña
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default PerfilPage;
